import React, { useEffect, useMemo, useState } from "react";
import styles from "./GitDiffPage.module.css";
import Header from "../../CustomWidgetBuilder/header";

const shouldHideChange = (change: any) => {
  const oldLine = change.oldLine?.trim() || "";
  const newLine = change.newLine?.trim() || "";
  const isNoiseLine = (line: string) => line.includes(": null");
  
  return (
    (isNoiseLine(oldLine) && !change.newLine) ||
    (isNoiseLine(newLine) && !change.oldLine) ||
    (isNoiseLine(oldLine) && isNoiseLine(newLine))
  );
};

function GitDiffPage() {
  const [fileDiffs] = useState<any[]>(() =>
    JSON.parse(sessionStorage.getItem("git_file_diffs") || "[]")
  );
  
  // State to track collapsed files using the raw fileName as the key
  const [collapsedFiles, setCollapsedFiles] = useState<Record<string, boolean>>({});

  const params = new URLSearchParams(window.location.search);
  const selectedFile = params.get("file");

  const sortedFileDiffs = useMemo(() => {
    return [...fileDiffs]
      .sort((a, b) =>
        a.fileName.localeCompare(b.fileName, undefined, {
          sensitivity: "base",
        }),
      )
      .map((file) => ({
        ...file,
        changes: [...(file.changes || [])].sort(
          (a, b) => Number(a.lineNumber || 0) - Number(b.lineNumber || 0),
        ),
      }));
  }, [fileDiffs]);

  const toggleFile = (fileName: string) => {
    setCollapsedFiles((prev) => ({
      ...prev,
      [fileName]: !prev[fileName], // FIXED: Corrected object assignment
    }));
  };

  useEffect(() => {
    if (!selectedFile) return;

    const elementId = `file-${encodeURIComponent(selectedFile)}`;
    const element = document.getElementById(elementId);

    if (element) {
      // FIXED: Corrected object assignment to uncollapse the selected file
      setCollapsedFiles((prev) => ({
        ...prev,
        [selectedFile]: false, 
      }));

      setTimeout(() => {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    }
  }, [selectedFile]);

  return (
    <div className={styles.gitDiffPage}>
      <div className={styles.headerContainer}>
        <Header />
      </div>
      <div className={styles.content}>
        {!sortedFileDiffs.length ? (
          <div className={styles.gitDiffEmpty}>No changes found</div>
        ) : (
          sortedFileDiffs.map((file: any) => {
            const visibleChanges = (file.changes || []).filter(
              (change: any) => !shouldHideChange(change),
            );

            return (
              <div
                id={`file-${encodeURIComponent(file.fileName)}`}
                key={file.fileName}
                className={styles.gitDiffFile}
              >
                <div
                  className={styles.gitDiffHeader}
                  onClick={() => toggleFile(file.fileName)}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                  }}
                >
                  <span style={{ fontWeight: "600" }}>
                    {file.fileName}
                    <span style={{ fontWeight: "normal", marginLeft: "4px", color: "#666" }}>
                      ({file.changeType})
                    </span>
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ fontSize: "0.85em", color: "#666" }}>
                      {visibleChanges.length} change
                      {visibleChanges.length !== 1 ? "s" : ""}
                    </span>
                    <span style={{ width: "15px", display: "inline-block", fontSize: "12px" }}>
                      {collapsedFiles[file.fileName] ? "▶" : "▼"}
                    </span>
                  </div>
                </div>

                {!collapsedFiles[file.fileName] &&
                  visibleChanges.map((change: any, idx: number) => (
                    <div key={idx}>
                      {change.oldLine && change.oldLine !== "null" && (
                        <div className={`${styles.diffRow} ${styles.diffRowDelete}`}>
                          <div className={`${styles.diffLineNo} ${styles.diffLineNoDelete}`}>
                            {change.lineNumber}
                          </div>
                          <div className={`${styles.diffSymbol} ${styles.diffSymbolDelete}`}>-</div>
                          <div className={styles.diffContent}>{change.oldLine}</div>
                        </div>
                      )}
                      {change.newLine && change.newLine !== "null" && (
                        <div className={`${styles.diffRow} ${styles.diffRowAdd}`}>
                          <div className={`${styles.diffLineNo} ${styles.diffLineNoAdd}`}>
                            {change.lineNumber}
                          </div>
                          <div className={`${styles.diffSymbol} ${styles.diffSymbolAdd}`}>+</div>
                          <div className={styles.diffContent}>{change.newLine}</div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default GitDiffPage;